import React, { FC, createRef, useEffect, useState } from 'react';

import { ArrowUpOutlined } from '@ant-design/icons';
import { Chart } from 'chart.js';
import CountUp from 'react-countup';
import { T } from 'react-translator-component';

import { COLORS } from '../../../../utils/theme/colors';
import Card from '../../../molecules/Card';
import * as S from '../styled';

export type SustainabilityIndicatorProps = {
  co2Saved: number;
  treePercentageGoal: number;
  cultivatedTree: number;
};

const SustainabilityIndicator: FC<SustainabilityIndicatorProps> = ({
  co2Saved = 0,
  treePercentageGoal = 0,
  cultivatedTree = 0,
}) => {
  const [chart, setChart] = useState({});

  const [initializedChart, setInitializedChart] = useState(false);

  const chartRef = createRef();

  function removeData(chart) {
    chart.data.datasets.forEach((dataset) => {
      delete dataset.data;
    });
    chart.update();
  }

  function addDataset(chart, data) {
    chart.data.datasets.forEach((dataset) => {
      dataset.data = data;
    });
    chart.update();
  }

  useEffect(() => {
    const data = {
      labels: [T('Goal'), T('Remaining')],
      datasets: [
        {
          label: 'Dataset 1',
          data: [
            treePercentageGoal,
            100 - parseFloat(treePercentageGoal as any),
          ],
          backgroundColor: [COLORS.available, COLORS.unavailable],
        },
      ],
    };
    const config = {
      type: 'doughnut',
      data: data,
      options: {
        elements: {
          arc: {
            borderWidth: 0,
          },
        },
        cutout: '80%',
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: COLORS.stone,
            color: '#000',
            labelColor: '#000',
            labelTextColor: '#000',
            bodyColor: '#000',
            bodyFont: {
              size: 11,
              family: 'Roboto',
            },
            padding: 4,
            xAlign: 'left',
            yAlign: 'top',
            callbacks: {
              labelColor: function (tooltipItem) {
                return {
                  backgroundColor:
                    tooltipItem.dataset.backgroundColor[tooltipItem.dataIndex],
                  borderColor: COLORS.stone,
                };
              },
              label: (item) => {
                return `${item.label[0]}: ${item.parsed.toFixed(1)}%`;
              },
            },
          },
        },
      },
    };
    if (!initializedChart) {
      const myChartRef = (chartRef as any).current.getContext('2d');
      if (treePercentageGoal !== 0) {
        setChart(new Chart(myChartRef, config as any));
        setInitializedChart(true);
      }
    } else {
      removeData(chart);
      addDataset(chart, [treePercentageGoal, 100 - treePercentageGoal]);
    }
  }, [treePercentageGoal, chartRef, chart, initializedChart]);

  return (
    <Card
      title={T('Sustainability')}
      indicators={[
        {
          values: (
            <S.WrapperSustainabilityIcon>
              <CountUp
                start={0}
                end={-co2Saved}
                duration={3}
                decimals={2}
                separator='.'
                decimal=','
              />
            </S.WrapperSustainabilityIcon>
          ),
          labels: (
            <div className='description_registered_wrapper'>
              <ArrowUpOutlined style={{ color: '#78d78d' }} />
              <p className='description_registered'>Kgco2 economizados</p>
            </div>
          ),
        },
        {
          values: (
            <S.Div>
              <S.Wrapper>
                <div className='canvas'>
                  <canvas ref={chartRef as any} />
                </div>
                <div className='indicators--pie'>
                  <div className='indicators--pie-text'>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CountUp
                        className='number_registered--text'
                        start={0}
                        end={cultivatedTree}
                        duration={3}
                      />
                    </div>
                  </div>
                </div>
              </S.Wrapper>
            </S.Div>
          ),
          labels: (
            <div className='description_registered_wrapper'>
              <ArrowUpOutlined style={{ color: '#78d78d' }} />
              <p className='description_registered'>
                {renderCultivatedText(cultivatedTree)}
              </p>
            </div>
          ),
        },
      ]}
    />
  );
};

export default SustainabilityIndicator;

const renderCultivatedText = (cultivatedTree: number) => {
  if (cultivatedTree <= 1) {
    return T('Cultivated Tree');
  }

  return T('Cultivated Trees');
};
